import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, Plus, Edit3, Trash2, Save, X } from "lucide-react";

const CATEGORIES = ["training", "recovery", "nutrition", "mindset", "racing", "injury"];
const CAT_COLOR = {
  training: "#d7ff00",
  recovery: "#7fffd4",
  nutrition: "#ffb347",
  mindset: "#c084fc",
  racing: "#ff6b6b",
  injury: "#ff9500",
};

export default function AdminTips() {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // tip being edited or {} for new
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const reload = () => {
    setLoading(true);
    api.get("/admin/tips").then(r => setTips(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const startNew = () => setEdit({ title: "", body: "", category: "training" });
  const startEdit = (t) => setEdit({ ...t });

  const save = async () => {
    if (!edit.title?.trim() || !edit.body?.trim()) {
      toast.error("Title and body required");
      return;
    }
    setBusy(true);
    try {
      if (edit.id) {
        await api.patch(`/admin/tips/${edit.id}`, {
          title: edit.title.trim(),
          body: edit.body.trim(),
          category: edit.category,
        });
        toast.success("Tip updated");
      } else {
        await api.post("/admin/tips", {
          title: edit.title.trim(),
          body: edit.body.trim(),
          category: edit.category,
        });
        toast.success("Tip published to athletes");
      }
      setEdit(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tip "${t.title}"? Athletes will no longer see it.`)) return;
    try {
      await api.delete(`/admin/tips/${t.id}`);
      toast.success("Tip deleted");
      reload();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const filtered = filter === "all" ? tips : tips.filter(t => t.category === filter);
  const counts = CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: tips.filter(t => t.category === c).length }), {});

  return (
    <div data-testid="admin-tips">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">TIPS LIBRARY</div>
          <h1 className="font-display text-4xl uppercase">Daily <span className="text-[#d7ff00]">insights</span></h1>
          <p className="mt-2 text-sm text-[#bbb]">What you publish here shows up instantly on every athlete's Tips page. Add fresh content weekly to keep runners coming back.</p>
        </div>
        <button onClick={startNew} className="bg-[#d7ff00] text-black font-display tracking-wider uppercase px-4 py-2.5 hover:bg-white flex items-center gap-2" data-testid="new-tip-btn">
          <Plus className="w-4 h-4" /> New tip
        </button>
      </div>

      {/* Category filters */}
      <div className="mt-6 flex flex-wrap gap-1.5">
        <Chip active={filter === "all"} onClick={() => setFilter("all")} testid="tip-filter-all">All · {tips.length}</Chip>
        {CATEGORIES.map(c => (
          <Chip key={c} active={filter === c} onClick={() => setFilter(c)} color={CAT_COLOR[c]} testid={`tip-filter-${c}`}>
            {c.toUpperCase()} · {counts[c] || 0}
          </Chip>
        ))}
      </div>

      {/* Tips grid */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {loading && <div className="col-span-2 text-[#888] text-sm p-4">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="col-span-2 bg-[#0a0a0a] border border-[#1f1f1f] p-8 text-center">
            <Lightbulb className="w-8 h-8 text-[#444] mx-auto mb-2" />
            <div className="text-[#888] text-sm">No tips in this category yet.</div>
            <div className="text-[#666] text-xs mt-1 font-mono-jp uppercase tracking-widest">Click "New tip" to add one.</div>
          </div>
        )}
        {filtered.map(t => {
          const color = CAT_COLOR[t.category] || "#d7ff00";
          return (
            <div key={t.id} className="bg-[#0a0a0a] border border-[#1f1f1f] border-l-4 p-5 flex flex-col" style={{ borderLeftColor: color }} data-testid={`tip-row-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="font-mono-jp text-[10px] uppercase tracking-widest flex items-center gap-1.5" style={{ color }}>
                  <Lightbulb className="w-3 h-3" /> {t.category?.toUpperCase()}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(t)} className="text-[#bbb] hover:text-[#d7ff00] p-1" data-testid={`edit-tip-${t.id}`} aria-label="Edit"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(t)} className="text-[#ff3b30] hover:text-red-300 p-1" data-testid={`del-tip-${t.id}`} aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <div className="mt-2 font-display text-xl uppercase">{t.title}</div>
              <p className="mt-2 text-[#bbb] text-sm leading-relaxed">{t.body}</p>
              {t.created_at && (
                <div className="mt-3 pt-3 border-t border-[#1f1f1f] font-mono-jp text-[9px] uppercase tracking-widest text-[#666]">
                  {t.updated_at ? `Updated ${t.updated_at.slice(0, 10)}` : `Posted ${t.created_at.slice(0, 10)}`}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit modal */}
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={() => !busy && setEdit(null)} data-testid="tip-edit-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-2xl my-6 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1.5">
                  <Lightbulb className="w-3 h-3" /> {edit.id ? "EDIT TIP" : "NEW TIP"}
                </div>
                <h3 className="font-display text-2xl uppercase mt-1">{edit.title || "Untitled tip"}</h3>
              </div>
              {!busy && <button onClick={() => setEdit(null)} className="text-[#888]" data-testid="tip-edit-close"><X /></button>}
            </div>

            <label className="block font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Title *</label>
            <input
              value={edit.title || ""}
              onChange={e => setEdit({ ...edit, title: e.target.value })}
              placeholder="e.g. The 80/20 Rule"
              className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2.5 text-sm"
              data-testid="tip-edit-title"
            />

            <label className="mt-4 block font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Category</label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {CATEGORIES.map(c => {
                const active = edit.category === c;
                const color = CAT_COLOR[c];
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setEdit({ ...edit, category: c })}
                    className="px-3 py-1.5 font-mono-jp text-[10px] uppercase tracking-widest border transition-colors"
                    style={active ? { borderColor: color, color, background: `${color}1a` } : { borderColor: "#222", color: "#bbb" }}
                    data-testid={`tip-edit-cat-${c}`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>

            <label className="mt-4 block font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Body *</label>
            <textarea
              value={edit.body || ""}
              onChange={e => setEdit({ ...edit, body: e.target.value })}
              rows={5}
              placeholder="Write the tip content. Keep it short, actionable, and direct — 2-4 sentences works best."
              className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2.5 text-sm leading-relaxed"
              data-testid="tip-edit-body"
            />
            <div className="mt-1 font-mono-jp text-[9px] uppercase tracking-widest text-[#666] text-right">{(edit.body || "").length} chars</div>

            <button
              onClick={save}
              disabled={busy || !edit.title?.trim() || !edit.body?.trim()}
              className="mt-5 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="tip-edit-save"
            >
              <Save className="w-4 h-4" /> {busy ? "Saving…" : (edit.id ? "Save changes" : "Publish to athletes")}
            </button>
            {!edit.id && (
              <p className="mt-2 text-[10px] font-mono-jp text-[#666] uppercase tracking-widest text-center">
                Goes live instantly on every athlete's Tips page.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children, color = "#d7ff00", testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-3 py-1.5 font-mono-jp text-[10px] uppercase tracking-widest border transition-colors"
      style={active
        ? { borderColor: color, color, background: `${color}1a` }
        : { borderColor: "#222", color: "#bbb" }}
    >
      {children}
    </button>
  );
}
